import { Redirect } from 'react-router-dom';
import { useMsal, useIsAuthenticated, UnauthenticatedTemplate } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Flex, Box, Link, Button, Container, Heading, Text, Divider } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { useIntl, FormattedMessage } from 'react-intl';

import Logo from '../components/utils/Logo';
import SplashScreen from '../components/layout/SplashScreen';
import Footer from '../components/layout/Footer';

import logoUrl from '../images/ascend-analytics-logo.png';

const Login = () => {
    const intl = useIntl();
    const { instance, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();

    if (isAuthenticated) {
        return <Redirect to={{ pathname: '/', search: window.location.search }} />;
    }

    return (
        <>
            <SplashScreen />

            <Wrapper>
                <UnauthenticatedTemplate>
                    <Main>
                        <PowerLogo />

                        <Container mt={6} flexGrow="1" display="flex" flexDirection="column">
                            <Heading variant="h2" textAlign="center">
                                <FormattedMessage id="login_sign_in_title" />
                            </Heading>

                            <Text size="md" textAlign="center" mt={2} mb={6}>
                                <FormattedMessage id="login_sign_in_desc" />
                            </Text>

                            <Button
                                isDisabled={inProgress === InteractionStatus.Login}
                                isFullWidth
                                mt="auto"
                                mb={6}
                                height={12}
                                onClick={() => instance.loginRedirect()}
                            >
                                <FormattedMessage id="login_sign_in_btn" />
                            </Button>

                            <Box position="relative" mb={6}>
                                <StyledDivider />

                                <Flex justifyContent="center">
                                    <Flex alignItems="flex-end" bg="white" position="relative" pl={2} pr={1}>
                                        <Text size="xs" variant="regular">
                                            <FormattedMessage id="login_powered_by" />
                                        </Text>

                                        <AscendLogo
                                            src={logoUrl}
                                            height="40"
                                            alt={intl.formatMessage({ id: 'common_ascend_logo' })}
                                        />
                                    </Flex>
                                </Flex>
                            </Box>

                            <Text size="xs" variant="regular" textAlign="center">
                                <FormattedMessage id="login_trademark" />
                            </Text>

                            <Link
                                mt={2}
                                display="block"
                                textAlign="center"
                                size="sm"
                                href="https://www.ascendanalytics.com/"
                            >
                                <FormattedMessage id="login_privacy_policy" />
                            </Link>

                            <Footer bg="transparent" color="gray.600" pt={2} pb={0} />
                        </Container>
                    </Main>
                </UnauthenticatedTemplate>
            </Wrapper>
        </>
    );
};

const Wrapper = styled.div`
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    overflow: auto;
    animation: 3s ease 0s normal forwards 1 entry;

    @keyframes entry {
        0% {
            transform: translateY(100vh);
        }
        66% {
            transform: translateY(100vh);
        }
        100% {
            transform: translateY(0);
        }
    }
`;

const Main = styled.div`
    width: 100%;
    background-color: var(--chakra-colors-white);
    padding: 16px;
    flex-grow: 1;
    display: flex;
    flex-direction: column;

    @media screen and (min-width: 48em) {
        flex-grow: 0;
        max-width: 480px;
        border-radius: var(--chakra-radii-lg);
        padding: 48px;
        margin: auto;
    }
`;

const StyledDivider = styled(Divider)`
    position: absolute;
    bottom: 0.5em;
`;

const AscendLogo = styled.img`
    height: 40px;
`;

const PowerLogo = styled(Logo)`
    width: 169px;
    height: 69px;
    margin: 12px auto 0;
`;

export default Login;
